import React, { useEffect } from 'react';
import { Grid, Button, Container } from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import Can from 'components/Can';
import Feature from 'components/Feature';
import i18n from 'i18n-js';
import { useStyles } from './ReservationRulesetCreate.styled';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { reservationRulesetsActions } from 'state/modules/reservationRulesets';
import {
  ReservationRulesetDetailsWidget,
  RulesetTabs,
  ReservationRulesetVariableBufferWidget,
} from './Components';
import { buildUrl } from 'utils';
import { getInitialBuffer } from './utils';
import { useConfirm } from 'hooks';
import { ability, abilityConfig } from 'config/ability';

export const ReservationRulesetDetails = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { currentRuleset } = useSelector((state) => state.reservationRulesets);
  const deleteSuccess = useSelector((state) => state.reservationRulesets.deleteSuccess);
  const { ruleset: beRuleset, isFetching, loading: loadingRRS } = currentRuleset;

  const [toggleDeleteRR, DeleteRRConfirm] = useConfirm({
    title: i18n.t('reservation_rulesets.delete'),
    message: i18n.t('reservation_rulesets.confirm_delete'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(reservationRulesetsActions.deleteReservationRuleset(id));
    },
  });

  const loading = isFetching || loadingRRS;

  useEffect(() => {
    if (!isNaN(id)) {
      dispatch(reservationRulesetsActions.fetchReservationRulesetById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(reservationRulesetsActions.resetCurrentRuleset());
      history.push(buildUrl(`/reservation-rulesets`));
    }
  }, [deleteSuccess]);

  const ruleset = {
    ...beRuleset,
    driver_rules: {
      ...beRuleset.driver_rules,
      multiple_maintenance_buffer: getInitialBuffer(beRuleset),
    },
  };
  const title = ruleset.name || '--';

  const canReadReservationRuleset = ability.can('read', 'reservation_rules_set');
  const isMultipleBufferEnabled = abilityConfig.can('multiple_buffer.enabled', 'reservation');
  const canUpdateMultipleBuffer = ability.can('update_multiple_buffer', 'reservation_rules_set');

  const hasConfig = useSelector((state) => state.config.settings.core !== null);

  //TODO: DO this at the router level, no need to load the componet/make request if the user does not have access
  const redirectToNotFound =
    !canReadReservationRuleset && (isMultipleBufferEnabled ? !canUpdateMultipleBuffer : true);

  useEffect(() => {
    if (redirectToNotFound && hasConfig) {
      history.push(buildUrl('/404'));
    }
  }, [redirectToNotFound, history, hasConfig]);

  return (
    <Layout title={title}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center">
              <Grid item style={{ marginRight: 'auto' }}>
                <Title title={title} />
              </Grid>
              <Can I="delete" a="reservation_rules_set">
                <Grid item>
                  <Button
                    data-cy="rrs_detail-delete-button"
                    style={{ textTransform: 'capitalize' }}
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      toggleDeleteRR();
                    }}
                    disabled={loading}
                  >
                    {i18n.t('common.delete')}
                  </Button>
                </Grid>
              </Can>

              <Can I="create" a="reservation_rules_set">
                <Grid item>
                  <Button
                    data-cy="rrs_detail-duplicate-button"
                    style={{ textTransform: 'capitalize' }}
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      dispatch(reservationRulesetsActions.resetCurrentRuleset());
                      history.push(buildUrl(`/reservation-rulesets/${id}/clone`));
                    }}
                    disabled={loading}
                  >
                    {i18n.t('common.duplicate')}
                  </Button>
                </Grid>
              </Can>
              <Feature name="reservation" is="multiple_buffer.enabled">
                <Can I="update_multiple_buffer" on="reservation_rules_set">
                  <Grid item>
                    <Button
                      data-cy="rrs_detail-update-button"
                      style={{ textTransform: 'capitalize' }}
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        history.push(buildUrl(`/reservation-rulesets/${id}/update`));
                      }}
                      disabled={loading}
                    >
                      {i18n.t('common.update')}
                    </Button>
                  </Grid>
                </Can>
              </Feature>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '1.5rem' }}>
            <span style={{ maxWidth: '40rem', display: 'block' }}>
              {i18n.t('reservation_rulesets.details_description_line1')}
            </span>
            {isMultipleBufferEnabled && canUpdateMultipleBuffer ? (
              <span style={{ maxWidth: '40rem', display: 'block' }}>
                {i18n.t('reservation_rulesets.details_description_line2')}
              </span>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={isMultipleBufferEnabled && canUpdateMultipleBuffer ? 4 : 12}>
            <ReservationRulesetDetailsWidget reservationRuleset={ruleset} loading={loading} />
          </Grid>
          {isMultipleBufferEnabled && canUpdateMultipleBuffer ? (
            <Grid item xs={12} sm={8}>
              <ReservationRulesetVariableBufferWidget
                ruleset={ruleset}
                loading={loading}
                editable={false}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <RulesetTabs reservationRuleset={ruleset} />
          </Grid>
        </Grid>
        <DeleteRRConfirm />
      </Container>
    </Layout>
  );
};
